/**
 * useDashboardLayout — per-user dashboard customization persisted server-side
 * (one row per user, GET/PUT /dashboard/layout) so a user's arrangement
 * follows them across devices. Replaces the earlier localStorage-per-device
 * model; a one-time migration lifts any pre-existing localStorage layout onto
 * the server on first load.
 *
 * Persists three things inside the layout blob:
 *  - widget order          → order
 *  - hidden widget ids     → hidden
 *  - enabled quick links   → quickLinks
 *
 * Stored values are reconciled against the currently-available widgets/actions
 * on read and on reorder (mergeOrder), so widgets added or removed in future
 * releases appear/disappear correctly without wiping a user's arrangement.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { arrayMove } from '@dnd-kit/sortable';
import type { DragEndEvent } from '@dnd-kit/core';
import { api } from '@/lib/api';
import { queryKeys } from '@/hooks/queryKeys';
import { QUICK_ACTION_IDS } from '@/components/dashboard/quickActions';
import type { DashboardWidgetId } from './dashboardWidgets';

/** Legacy per-device localStorage keys, read once for migration then cleared. */
const LEGACY_ORDER_KEY = 'vs-dashboard-widget-order';
const LEGACY_HIDDEN_KEY = 'vs-dashboard-widget-hidden';
const LEGACY_QUICKLINKS_KEY = 'vs-dashboard-quicklinks';

/** Shape persisted server-side (all fields optional; client sends full arrays). */
interface StoredLayout {
  order?: string[];
  hidden?: string[];
  quickLinks?: string[];
}

function readLegacyArray(key: string): string[] | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((v): v is string => typeof v === 'string')
      : null;
  } catch {
    return null;
  }
}

/** Read a legacy localStorage layout, if any. Returns null when nothing stored. */
function readLegacyLayout(): StoredLayout | null {
  const order = readLegacyArray(LEGACY_ORDER_KEY);
  const hidden = readLegacyArray(LEGACY_HIDDEN_KEY);
  const quickLinks = readLegacyArray(LEGACY_QUICKLINKS_KEY);
  if (order === null && hidden === null && quickLinks === null) return null;
  return {
    ...(order !== null ? { order } : {}),
    ...(hidden !== null ? { hidden } : {}),
    ...(quickLinks !== null ? { quickLinks } : {}),
  };
}

function clearLegacyLayout(): void {
  try {
    localStorage.removeItem(LEGACY_ORDER_KEY);
    localStorage.removeItem(LEGACY_HIDDEN_KEY);
    localStorage.removeItem(LEGACY_QUICKLINKS_KEY);
  } catch {
    // Ignore localStorage failures (private mode, quota, etc.).
  }
}

/** Keep stored order, drop unknown ids, append newly-available ids at the end. */
function mergeOrder(order: string[], available: string[]): string[] {
  const filtered = order.filter((id) => available.includes(id));
  const missing = available.filter((id) => !filtered.includes(id));
  return [...filtered, ...missing];
}

export interface DashboardLayoutParams {
  /** Widget ids the user is permitted to see, in catalog order. */
  availableWidgetIds: DashboardWidgetId[];
  /** Widget ids visible by default for this user's role. */
  defaultVisibleIds: DashboardWidgetId[];
}

export interface DashboardLayout {
  /** All available widgets in the user's chosen order (reconciled). */
  orderedWidgetIds: DashboardWidgetId[];
  isWidgetHidden: (id: DashboardWidgetId) => boolean;
  toggleWidget: (id: DashboardWidgetId) => void;
  reorderWidgets: (event: DragEndEvent) => void;
  isQuickLinkEnabled: (id: string) => boolean;
  enabledQuickLinkIds: string[];
  toggleQuickLink: (id: string) => void;
  resetToDefault: () => void;
}

export function useDashboardLayout({
  availableWidgetIds,
  defaultVisibleIds,
}: DashboardLayoutParams): DashboardLayout {
  const queryClient = useQueryClient();

  const defaultHiddenIds = useMemo(
    () => availableWidgetIds.filter((id) => !defaultVisibleIds.includes(id)),
    [availableWidgetIds, defaultVisibleIds],
  );

  const { data: serverLayout } = useQuery({
    queryKey: queryKeys.dashboard.layout(),
    queryFn: () => api.get<StoredLayout>('/dashboard/layout'),
    staleTime: 5 * 60 * 1000,
  });

  const saveMutation = useMutation({
    mutationFn: (layout: StoredLayout) => api.put<StoredLayout>('/dashboard/layout', layout),
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKeys.dashboard.layout(), saved);
    },
  });
  // Keep a stable ref so the persist helper isn't a hook dependency.
  const saveRef = useRef(saveMutation.mutate);
  saveRef.current = saveMutation.mutate;

  // Local working copy. `null` = not yet seeded from the server response.
  const [widgetOrder, setWidgetOrder] = useState<string[] | null>(null);
  const [hiddenWidgets, setHiddenWidgets] = useState<string[] | null>(null);
  const [enabledQuickLinks, setEnabledQuickLinks] = useState<string[] | null>(null);

  // Seed local state once the server layout resolves. If the server has nothing
  // but a legacy localStorage layout exists, migrate it up (once) and clear it.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || serverLayout === undefined) return;
    seededRef.current = true;

    const hasServerLayout =
      serverLayout.order !== undefined ||
      serverLayout.hidden !== undefined ||
      serverLayout.quickLinks !== undefined;

    const source = hasServerLayout ? serverLayout : (readLegacyLayout() ?? {});

    const order = source.order ?? availableWidgetIds;
    const hidden = source.hidden ?? defaultHiddenIds;
    const quickLinks = source.quickLinks ?? QUICK_ACTION_IDS;

    setWidgetOrder(order);
    setHiddenWidgets(hidden);
    setEnabledQuickLinks(quickLinks);

    // Migrate a legacy localStorage layout to the server exactly once.
    if (!hasServerLayout && readLegacyLayout() !== null) {
      saveRef.current({ order, hidden, quickLinks });
      clearLegacyLayout();
    }
  }, [serverLayout, availableWidgetIds, defaultHiddenIds]);

  // Effective values with defaults applied while state is still un-seeded.
  const effectiveOrder = widgetOrder ?? availableWidgetIds;
  const effectiveHidden = hiddenWidgets ?? defaultHiddenIds;
  const effectiveQuickLinks = enabledQuickLinks ?? QUICK_ACTION_IDS;

  /** Persist the full layout, mirroring the local optimistic update. */
  const persist = useCallback(
    (next: { order: string[]; hidden: string[]; quickLinks: string[] }) => {
      saveRef.current(next);
    },
    [],
  );

  const orderedWidgetIds = useMemo(
    () => mergeOrder(effectiveOrder, availableWidgetIds) as DashboardWidgetId[],
    [effectiveOrder, availableWidgetIds],
  );

  const isWidgetHidden = useCallback(
    (id: DashboardWidgetId) => effectiveHidden.includes(id),
    [effectiveHidden],
  );

  const toggleWidget = useCallback(
    (id: DashboardWidgetId) => {
      setHiddenWidgets((cur) => {
        const base = cur ?? defaultHiddenIds;
        const next = base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
        persist({
          order: mergeOrder(widgetOrder ?? availableWidgetIds, availableWidgetIds),
          hidden: next,
          quickLinks: enabledQuickLinks ?? QUICK_ACTION_IDS,
        });
        return next;
      });
    },
    [defaultHiddenIds, persist, widgetOrder, availableWidgetIds, enabledQuickLinks],
  );

  const reorderWidgets = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      setWidgetOrder((cur) => {
        const sanitized = mergeOrder(cur ?? availableWidgetIds, availableWidgetIds);
        const oldIndex = sanitized.indexOf(String(active.id));
        const newIndex = sanitized.indexOf(String(over.id));
        if (oldIndex === -1 || newIndex === -1) return sanitized;
        const next = arrayMove(sanitized, oldIndex, newIndex);
        persist({
          order: next,
          hidden: hiddenWidgets ?? defaultHiddenIds,
          quickLinks: enabledQuickLinks ?? QUICK_ACTION_IDS,
        });
        return next;
      });
    },
    [availableWidgetIds, persist, hiddenWidgets, defaultHiddenIds, enabledQuickLinks],
  );

  const enabledQuickLinkIds = useMemo(
    () => effectiveQuickLinks.filter((id) => QUICK_ACTION_IDS.includes(id)),
    [effectiveQuickLinks],
  );

  const isQuickLinkEnabled = useCallback(
    (id: string) => effectiveQuickLinks.includes(id),
    [effectiveQuickLinks],
  );

  const toggleQuickLink = useCallback(
    (id: string) => {
      setEnabledQuickLinks((cur) => {
        const base = cur ?? QUICK_ACTION_IDS;
        const next = base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
        persist({
          order: mergeOrder(widgetOrder ?? availableWidgetIds, availableWidgetIds),
          hidden: hiddenWidgets ?? defaultHiddenIds,
          quickLinks: next,
        });
        return next;
      });
    },
    [persist, widgetOrder, availableWidgetIds, hiddenWidgets, defaultHiddenIds],
  );

  const resetToDefault = useCallback(() => {
    setWidgetOrder(availableWidgetIds);
    setHiddenWidgets(defaultHiddenIds);
    setEnabledQuickLinks(QUICK_ACTION_IDS);
    persist({
      order: availableWidgetIds,
      hidden: defaultHiddenIds,
      quickLinks: QUICK_ACTION_IDS,
    });
  }, [availableWidgetIds, defaultHiddenIds, persist]);

  return {
    orderedWidgetIds,
    isWidgetHidden,
    toggleWidget,
    reorderWidgets,
    isQuickLinkEnabled,
    enabledQuickLinkIds,
    toggleQuickLink,
    resetToDefault,
  };
}
